import { useEffect, useMemo, useRef, useState } from 'react';
import { shortcut } from '@/lib/format';

export interface Command {
  readonly id: string;
  readonly label: string;
  readonly group: string;
  readonly hint?: string;
  readonly keys?: string;
  readonly run: () => void;
}

export interface CommandPaletteProps {
  readonly commands: readonly Command[];
  readonly onClose: () => void;
}

/** Subsequence match, so "rnc" finds "Run cell". */
function score(command: Command, needle: string): number {
  if (needle === '') return 1;
  const haystack = `${command.group} ${command.label}`.toLowerCase();
  if (haystack.includes(needle)) return 100 - haystack.indexOf(needle);

  let index = 0;
  for (const character of needle) {
    index = haystack.indexOf(character, index);
    if (index === -1) return 0;
    index += 1;
  }
  return 1;
}

export function CommandPalette({ commands, onClose }: CommandPaletteProps): JSX.Element {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return commands
      .map((command) => ({ command, rank: score(command, needle) }))
      .filter((entry) => entry.rank > 0)
      .sort((a, b) => b.rank - a.rank)
      .map((entry) => entry.command);
  }, [commands, query]);

  useEffect(() => setActive(0), [query]);

  useEffect(() => {
    const node = listRef.current?.querySelector<HTMLElement>('[data-active="true"]');
    node?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const run = (command: Command | undefined): void => {
    if (!command) return;
    onClose();
    command.run();
  };

  const onKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((current) => Math.min(matches.length - 1, current + 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((current) => Math.max(0, current - 1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      run(matches[active]);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
  };

  let lastGroup = '';

  return (
    <div className="overlay" role="presentation" onMouseDown={onClose}>
      <div
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <input
          // eslint-disable-next-line jsx-a11y/no-autofocus -- a palette that does
          // not take focus on open is broken; it is opened by an explicit shortcut.
          autoFocus
          className="w-full border-b border-line bg-transparent px-3 py-2.5 text-md text-ink outline-none placeholder:text-faint"
          placeholder="Run a command…"
          aria-label="Command"
          aria-controls="palette-list"
          aria-activedescendant={matches[active] ? `cmd-${matches[active].id}` : undefined}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onKeyDown}
        />

        <div
          id="palette-list"
          ref={listRef}
          role="listbox"
          aria-label="Commands"
          className="max-h-80 overflow-auto py-1"
        >
          {matches.map((command, index) => {
            const header = command.group !== lastGroup ? command.group : null;
            lastGroup = command.group;

            return (
              <div key={command.id}>
                {header ? <div className="eyebrow px-3 pb-0.5 pt-2">{header}</div> : null}
                <div
                  id={`cmd-${command.id}`}
                  role="option"
                  aria-selected={index === active}
                  data-active={index === active}
                  className="flex cursor-pointer items-center gap-2 px-3 py-1.5"
                  style={{
                    background: index === active ? 'var(--c-selection)' : 'transparent',
                  }}
                  onMouseEnter={() => setActive(index)}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    run(command);
                  }}
                >
                  <span className="truncate text-sm text-ink">{command.label}</span>
                  {command.hint ? (
                    <span className="truncate text-[10px] text-faint">{command.hint}</span>
                  ) : null}
                  {command.keys ? (
                    <kbd className="kbd ml-auto shrink-0">{shortcut(command.keys)}</kbd>
                  ) : null}
                </div>
              </div>
            );
          })}

          {matches.length === 0 ? (
            <p className="px-3 py-6 text-center text-xs text-muted">
              No command matches “{query}”.
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
